import { useState, useEffect } from "react";
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/context/ThemeContext";
import { UserPlus } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { UserType } from "@/types/user";
import AccountTypeSelector from "./AccountTypeSelector";
import DealerRegistrationForm from "./registration/DealerRegistrationForm";
import ConsumerRegistrationForm from "./registration/ConsumerRegistrationForm";
import BusinessRegistrationForm from "./registration/BusinessRegistrationForm";
import EmailOtpStep from "./EmailOtpStep";
import { AuthService, RegisterData } from "@/services/auth/authService";
import { toast } from "sonner";
import { authLabels, commonButtonLabels } from "@/i18n";
import { getEmailOtpErrorMessage } from "@/lib/auth/emailOtpErrors";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { cn } from "@/lib/utils";
import {
  openBankIdInSystemBrowser,
  resolveApiAbsoluteUrl,
} from "@/lib/native/bankIdBrowser";
import { isNativeCapacitorApp } from "@/integrations/clients/carAuction/nativeAuth";

interface RegisterDialogProps {
  onClose: () => void;
  open?: boolean;
  defaultUserType?: UserType | undefined;
}

type RegisterMethod = "bankid" | "manual" | null;
type RegisterStep =
  | "method"
  | "select"
  | "dealer"
  | "consumer"
  | "business"
  | "otp"
  | "dealer-info"
  | "business-info";

const formatMmSs = (totalSeconds: number) => {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

const RegisterDialog = ({
  onClose,
  open,
  defaultUserType,
}: RegisterDialogProps) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const { isNative } = useNativePlatform();
  const {
    registerAsync,
    isRegistering,
    registerError,
    resetRegister,
  } = useAuth();

  const authLabels_i18n = authLabels();
  const buttonLabels = commonButtonLabels();
  const bankIdRegister = authLabels_i18n.bankIdRegister ?? {};

  const [method, setMethod] = useState<RegisterMethod>(null);
  const [step, setStep] = useState<RegisterStep>("method");
  const [formKey, setFormKey] = useState(0);
  const [pendingFormData, setPendingFormData] = useState<Record<string, unknown> | null>(null);
  const [otpCode, setOtpCode] = useState("");
  const [otpProof, setOtpProof] = useState<string | null>(null);
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [bankIdStarting, setBankIdStarting] = useState(false);
  const [otpExpiresAtMs, setOtpExpiresAtMs] = useState<number | null>(null);
  const [otpSecondsLeft, setOtpSecondsLeft] = useState(0);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    resetRegister();
  }, [resetRegister, step]);

  useEffect(() => {
    if (!otpExpiresAtMs) {
      setOtpSecondsLeft(0);
      return;
    }
    const tick = () => {
      const left = Math.max(0, Math.ceil((otpExpiresAtMs - Date.now()) / 1000));
      setOtpSecondsLeft(left);
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [otpExpiresAtMs]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = window.setTimeout(
      () => setResendCooldown((s) => Math.max(0, s - 1)),
      1000,
    );
    return () => window.clearTimeout(id);
  }, [resendCooldown]);

  const resolveTypeStep = (userType?: UserType): RegisterStep => {
    if (userType === UserType.Business) return "business";
    if (userType === UserType.Dealer) return "dealer";
    if (userType === UserType.Individual) return "consumer";
    return "select";
  };

  useEffect(() => {
    if (open) {
      setMethod(null);
      setStep("method");
      setPendingFormData(null);
      setOtpCode("");
      setOtpProof(null);
      setOtpExpiresAtMs(null);
      setResendCooldown(0);
      setFormKey((k) => k + 1);
    }
  }, [open, defaultUserType]);

  const handleMethodSelect = (next: "bankid" | "manual") => {
    setMethod(next);
    setStep(resolveTypeStep(defaultUserType));
  };

  const handleAccountTypeSelect = (
    type: "dealer" | "consumer" | "business",
  ) => {
    setStep(type);
  };

  const handleBackToMethod = () => {
    setMethod(null);
    setStep("method");
    setPendingFormData(null);
    setOtpCode("");
    setOtpProof(null);
    setOtpExpiresAtMs(null);
    setResendCooldown(0);
  };

  const handleBackToSelection = () => {
    setStep("select");
    setPendingFormData(null);
    setOtpCode("");
    setOtpProof(null);
    setOtpExpiresAtMs(null);
    setResendCooldown(0);
  };

  const sendOtp = async (email: string) => {
    setOtpSending(true);
    try {
      const result = await AuthService.sendEmailOtp(email, "register");
      const expiresAt = result.expiresAt
        ? Date.parse(result.expiresAt)
        : Date.now() + (result.expiresInSeconds ?? 600) * 1000;
      setOtpExpiresAtMs(expiresAt);
      setResendCooldown(30);
      toast.success(bankIdRegister.otp?.sent || "Verifieringskod skickad");
    } catch (error: any) {
      toast.error(
        getEmailOtpErrorMessage(
          error,
          {
            emailExists:
              bankIdRegister.otp?.emailExists ||
              bankIdRegister.errors?.emailExists ||
              authLabels_i18n.error?.accountExists,
            emailPending:
              authLabels_i18n.error?.emailPending ||
              bankIdRegister.errors?.emailPending,
            rateLimited: bankIdRegister.otp?.rateLimited,
            sendError: bankIdRegister.otp?.sendError,
            generic: authLabels_i18n.error?.somethingWentWrong,
          },
          "send",
        ),
      );
      throw error;
    } finally {
      setOtpSending(false);
    }
  };

  const handleRegistrationSubmit = async (data: Record<string, unknown>) => {
    // BankID and manual consumer: OTP before create/start
    if (method === "bankid" || data.type === "consumer") {
      setPendingFormData(data);
      setOtpCode("");
      setOtpProof(null);
      try {
        await sendOtp(data.email as string);
        setStep("otp");
      } catch {
        // Stay on the form step — sendOtp already showed the toast
        // (e.g. EMAIL_PENDING / already registered).
      }
      return;
    }

    // Dealer/business manual: application without OTP
    try {
      const payload: RegisterData = {
        personalInfo: {
          name: data.username as string,
          email: data.email as string,
          password: data.password as string,
          confirmPassword:
            (data.confirmPassword as string) || (data.password as string),
          type: data.type as "dealer" | "consumer" | "business",
          phone: data.phoneNumber as string,
        },
        message: (data.message as string) || undefined,
        companyId: data.companyId as string | undefined,
      };

      await registerAsync(payload);

      toast.success(authLabels_i18n.success.accountCreated, {
        duration: 4000,
      });

      setFormKey((prev) => prev + 1);

      if (data.type === "dealer") {
        setStep("dealer-info");
      } else if (data.type === "business") {
        setStep("business-info");
      }
    } catch (error: unknown) {
      const apiError = error as { status?: number; errorCode?: string; message?: string };
      if (apiError?.errorCode === "EMAIL_PENDING") {
        toast.error(
          authLabels_i18n.error?.emailPending ||
            "Det finns redan en pågående kontoförfrågan med den e-postadressen.",
          { duration: 6000 },
        );
      } else if (apiError?.errorCode === "SSN_PENDING") {
        toast.error(
          authLabels_i18n.error?.ssnPending ||
            "Det finns redan en pågående kontoförfrågan med det personnumret.",
          { duration: 6000 },
        );
      } else if (apiError?.status === 409) {
        const errorMessage =
          apiError.message ||
          authLabels_i18n.error.accountExists;
        toast.error(errorMessage, { duration: 5000 });
      } else {
        const errorMessage =
          apiError?.message ||
          authLabels_i18n.error.somethingWentWrong;
        const fullErrorMessage =
          authLabels_i18n.error.registrationFailed.replace(
            "{message}",
            errorMessage || "",
          );
        toast.error(fullErrorMessage, { duration: 5000 });
      }
    }
  };

  const completeManualRegister = async (proof: string) => {
    if (!pendingFormData) return;
    const payload: RegisterData = {
      personalInfo: {
        name: pendingFormData.username as string,
        email: pendingFormData.email as string,
        password: pendingFormData.password as string,
        confirmPassword:
          (pendingFormData.confirmPassword as string) ||
          (pendingFormData.password as string),
        type: "consumer",
        phone: pendingFormData.phoneNumber as string,
      },
      message: (pendingFormData.message as string) || undefined,
      otpProof: proof,
    };

    await registerAsync(payload);
    toast.success(authLabels_i18n.success.accountCreated, {
      duration: 4000,
    });
    setFormKey((prev) => prev + 1);
    setStep("method");
    onClose();
  };

  const handleVerifyOtp = async (codeOverride?: string) => {
    if (!pendingFormData) return;
    const email = pendingFormData.email as string;
    const code = (codeOverride ?? otpCode).trim();
    if (!/^\d{6}$/.test(code)) {
      toast.error(bankIdRegister.otp?.invalidCode || "Ange 6 siffror");
      return;
    }

    setOtpVerifying(true);
    try {
      const result = await AuthService.verifyEmailOtp(email, code, "register");
      if (!result.otpProof) {
        throw new Error(bankIdRegister.otp?.verifyError || "Ogiltig verifieringskod");
      }
      setOtpProof(result.otpProof);
      toast.success(bankIdRegister.otp?.verified || "E-post verifierad");

      if (method === "manual") {
        await completeManualRegister(result.otpProof);
      } else {
        await startBankIdWithProof(result.otpProof);
      }
    } catch (error: any) {
      toast.error(
        getEmailOtpErrorMessage(
          error,
          {
            emailExists:
              bankIdRegister.otp?.emailExists ||
              bankIdRegister.errors?.emailExists,
            rateLimited: bankIdRegister.otp?.rateLimited,
            alreadyUsed: bankIdRegister.otp?.alreadyUsed,
            expired: bankIdRegister.otp?.expired,
            invalidCode: bankIdRegister.otp?.invalidCode,
            verifyError: bankIdRegister.otp?.verifyError,
            generic: authLabels_i18n.error?.somethingWentWrong,
          },
          "verify",
        ),
      );
    } finally {
      setOtpVerifying(false);
    }
  };

  const startBankIdWithProof = async (proof: string) => {
    if (!pendingFormData) return;
    setBankIdStarting(true);
    try {
      const response = await AuthService.startBankIdRegister({
        username: (pendingFormData.username as string) || "",
        email: pendingFormData.email as string,
        phoneNumber: pendingFormData.phoneNumber as string,
        password: pendingFormData.password as string,
        type: pendingFormData.type as "dealer" | "consumer" | "business",
        companyId: pendingFormData.companyId as string | undefined,
        message: (pendingFormData.message as string) || undefined,
        otpProof: proof,
      });

      if (isNativeCapacitorApp() && response.continueUrl) {
        await openBankIdInSystemBrowser(response.continueUrl);
        setBankIdStarting(false);
        onClose();
        return;
      }

      const path = response.redirectUrl.startsWith("/")
        ? response.redirectUrl
        : `/${response.redirectUrl}`;
      window.location.replace(resolveApiAbsoluteUrl(path));
    } catch (error: any) {
      const code = error?.errorCode as string | undefined;
      toast.error(
        (code === "EMAIL_PENDING"
          ? authLabels_i18n.error?.emailPending
          : null) ||
          error?.message ||
          bankIdRegister.error ||
          "Kunde inte starta BankID-registrering",
      );
      setBankIdStarting(false);
    }
  };

  const renderContent = () => {
    switch (step) {
      case "method":
        return (
          <div className="grid gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => handleMethodSelect("bankid")}
              className="rounded-xl border border-primary/30 bg-primary/5 p-4 text-left transition hover:border-primary hover:bg-primary/10"
            >
              <div className="mb-3 flex items-center gap-2">
                <img
                  src="/assets/BankID_logo.png"
                  alt="BankID"
                  className="h-6 w-auto object-contain"
                />
                <span className="font-semibold">
                  {bankIdRegister.method?.bankIdTitle || "Med BankID"}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                {bankIdRegister.method?.bankIdDescription ||
                  "Snabbare registrering med BankID. Personnummer hämtas automatiskt."}
              </p>
            </button>
            <button
              type="button"
              onClick={() => handleMethodSelect("manual")}
              className="rounded-xl border border-border p-4 text-left transition hover:border-foreground/30 hover:bg-muted/40"
            >
              <div className="mb-3 flex items-center gap-2">
                <UserPlus className="h-5 w-5" />
                <span className="font-semibold">
                  {bankIdRegister.method?.manualTitle || "Manuellt"}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                {bankIdRegister.method?.manualDescription ||
                  "Skapa konto med e-post och lösenord. Verifiera identitet senare."}
              </p>
            </button>
          </div>
        );
      case "select":
        return (
          <div className="space-y-3">
            <Button variant="ghost" size="sm" onClick={handleBackToMethod}>
              {buttonLabels.back || "Tillbaka"}
            </Button>
            <AccountTypeSelector onSelectType={handleAccountTypeSelect} />
          </div>
        );
      case "dealer":
        return (
          <DealerRegistrationForm
            key={`dealer-${formKey}`}
            onBack={handleBackToSelection}
            onSubmit={handleRegistrationSubmit}
            isLoading={isRegistering || otpSending}
            error={registerError?.message}
            nameFromBankId={method === "bankid"}
          />
        );
      case "consumer":
        return (
          <ConsumerRegistrationForm
            key={`consumer-${formKey}`}
            onBack={handleBackToSelection}
            onSubmit={handleRegistrationSubmit}
            isLoading={isRegistering || otpSending}
            error={registerError?.message}
            nameFromBankId={method === "bankid"}
          />
        );
      case "business":
        return (
          <BusinessRegistrationForm
            key={`business-${formKey}`}
            onBack={handleBackToSelection}
            onSubmit={handleRegistrationSubmit}
            isLoading={isRegistering || otpSending}
            error={registerError?.message}
            nameFromBankId={method === "bankid"}
          />
        );
      case "otp":
        return (
          <div className="space-y-3">
            <EmailOtpStep
              email={String(pendingFormData?.email || "")}
              otpCode={otpCode}
              onOtpCodeChange={setOtpCode}
              onVerify={(code) => void handleVerifyOtp(code)}
              onResend={() =>
                pendingFormData?.email
                  ? void sendOtp(pendingFormData.email as string)
                  : undefined
              }
              onBack={handleBackToSelection}
              backLabel={buttonLabels.back || "Tillbaka"}
              otpSending={otpSending}
              otpVerifying={otpVerifying}
              continueBusy={bankIdStarting || isRegistering}
              otpSecondsLeft={otpSecondsLeft}
              otpExpiresAtMs={otpExpiresAtMs}
              resendCooldown={resendCooldown}
              formatMmSs={formatMmSs}
              labels={{
                stepHint:
                  bankIdRegister.otp?.stepHint ||
                  "Steg 3 av 3 — e-postverifiering",
                title: bankIdRegister.otp?.title || "Verifiera e-post",
                description: bankIdRegister.otp?.description,
                codeLabel: bankIdRegister.otp?.codeLabel,
                expiresIn: bankIdRegister.otp?.expiresIn,
                expired: bankIdRegister.otp?.expired,
                resend: bankIdRegister.otp?.resend,
                resendIn: bankIdRegister.otp?.resendIn,
                sending: bankIdRegister.otp?.sending,
                continue:
                  method === "manual"
                    ? bankIdRegister.otp?.continueManual ||
                      "Verifiera och skapa konto"
                    : bankIdRegister.otp?.continue ||
                      "Verifiera och öppna BankID",
                continuing: bankIdRegister.otp?.continuing,
              }}
            />
            {otpProof && method === "bankid" ? (
              <p className="text-center text-xs text-muted-foreground">
                {bankIdRegister.otp?.redirecting || "Öppnar BankID..."}
              </p>
            ) : null}
          </div>
        );
      case "dealer-info":
        return (
          <div className="space-y-4 p-2">
            <h3 className="text-lg font-semibold">
              {authLabels_i18n.dealerInfo.title}
            </h3>
            <p className="text-sm text-muted-foreground">
              {authLabels_i18n.dealerInfo.message}
            </p>
            <div className="flex justify-end">
              <button
                className="btn btn-primary px-4 py-2 rounded-md bg-primary text-primary-foreground"
                onClick={() => {
                  setStep("method");
                  onClose();
                }}
              >
                {authLabels_i18n.buttons.ok}
              </button>
            </div>
          </div>
        );
      case "business-info":
        return (
          <div className="space-y-4 p-2">
            <h3 className="text-lg font-semibold">
              {authLabels_i18n.businessInfo.title}
            </h3>
            <p className="text-sm text-muted-foreground">
              {authLabels_i18n.businessInfo.message}
            </p>
            <div className="flex justify-end">
              <button
                className="btn btn-primary px-4 py-2 rounded-md bg-primary text-primary-foreground"
                onClick={() => {
                  setStep("method");
                  onClose();
                }}
              >
                {authLabels_i18n.buttons.ok}
              </button>
            </div>
          </div>
        );
      default:
        return (
          <div className="grid gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => handleMethodSelect("bankid")}
              className="rounded-xl border p-4 text-left"
            >
              Med BankID
            </button>
            <button
              type="button"
              onClick={() => handleMethodSelect("manual")}
              className="rounded-xl border p-4 text-left"
            >
              Manuellt
            </button>
          </div>
        );
    }
  };

  return (
    <DialogContent
      sheetOpen={open}
      expandable={isNative}
      className={cn(
        "max-h-[90vh] overflow-y-auto",
        step === "otp" ? "sm:max-w-lg" : "sm:max-w-4xl",
        isNative && "native-register-sheet native-sheet-expandable native-sheet-form max-w-none w-full",
        isDark
          ? "bg-black/95 border-white/10 text-white"
          : "bg-white border-gray-200",
      )}
    >
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          {method === "bankid" ? (
            <>
              <img
                src="/assets/BankID_logo.png"
                alt="BankID"
                className="h-6 w-auto object-contain"
              />
              {bankIdRegister.titleBankId || "Skapa nytt konto - BankID"}
            </>
          ) : method === "manual" ? (
            <>
              <UserPlus className="h-5 w-5" />
              {bankIdRegister.titleManual || "Skapa nytt konto - Manuellt"}
            </>
          ) : (
            <>
              <UserPlus className="h-5 w-5" />
              {authLabels_i18n.title.createAccount}
            </>
          )}
        </DialogTitle>
        {(step === "method" || step === "select") && (
          <DialogDescription
            className={isDark ? "text-gray-400" : "text-gray-500"}
          >
            {step === "method"
              ? bankIdRegister.method?.description ||
                "Välj hur du vill skapa konto"
              : authLabels_i18n.description.selectAccountType}
          </DialogDescription>
        )}
      </DialogHeader>

      <div className="py-2">{renderContent()}</div>
    </DialogContent>
  );
};

export default RegisterDialog;
